import { useEffect, useMemo, useRef, useState } from 'react'
import {
	MenuList,
	MenuListDivider,
	ListItem,
	Input,
	Checkbox
} from '@clickguard/design-system'
import { AdAccountDTO } from 'api-models'
import { PlatformIcon } from 'components/platform-icon'
import Announcement02 from '@untitled-ui/icons-react/build/esm/Announcement02'
import { BreadcrumbTrigger } from './breadcrumb-trigger'
import { sortCampaigns } from './campaign-order'
import { CampaignStatusIcon } from './campaign-status-icon'
import { CampaignStatusPills } from './campaign-status-pills'
import {
	CAMPAIGN_STATUS_FILTERS,
	CampaignDTO,
	CampaignStatusFilter,
	countCampaignsByStatus,
	getCampaignStatusGroup
} from './campaign-status'

interface CampaignsSelectorProps {
	campaigns: CampaignDTO[]
	selectedCampaigns: CampaignDTO[]
	adAccounts: AdAccountDTO[]
	onApply: (campaigns: CampaignDTO[]) => void
}

export const CampaignsSelector = ({
	campaigns,
	selectedCampaigns,
	adAccounts,
	onApply
}: CampaignsSelectorProps) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const [draftSelection, setDraftSelection] = useState<CampaignDTO[]>([])
	const containerRef = useRef<HTMLDivElement>(null)

	// Build a lookup map: ad_account_id -> platform
	const adAccountPlatformMap = useMemo(() => {
		const map: Record<string, string> = {}
		adAccounts.forEach((acc) => {
			map[acc.id] = acc.platform
		})
		return map
	}, [adAccounts])

	const uniqueCampaigns = useMemo(() => {
		const seen = new Set<string>()
		return campaigns.filter((c) => {
			if (!c?.id || seen.has(c.id)) return false
			seen.add(c.id)
			return true
		})
	}, [campaigns])

	const totalCount = uniqueCampaigns.length
	const selectedCount = draftSelection.length

	const visibleCampaigns = useMemo(() => {
		const q = searchQuery.trim().toLowerCase()
		if (!q) return uniqueCampaigns
		return uniqueCampaigns.filter((c) =>
			(c.name ?? '').toLowerCase().includes(q)
		)
	}, [uniqueCampaigns, searchQuery])

	const sortedVisibleCampaigns = useMemo(
		() => sortCampaigns(visibleCampaigns, adAccountPlatformMap),
		[visibleCampaigns, adAccountPlatformMap]
	)

	// Active/Paused/Removed pill counts (per status group, across all campaigns)
	const statusCounts = useMemo(
		() => countCampaignsByStatus(uniqueCampaigns),
		[uniqueCampaigns]
	)

	// Whether each status group is fully / partially / not in the draft selection.
	// Mirrors the Ad-accounts platform-card selection state.
	const statusSelectionState = useMemo(() => {
		const state: Record<CampaignStatusFilter, 'all' | 'some' | 'none'> = {
			ACTIVE: 'none',
			PAUSED: 'none',
			REMOVED: 'none'
		}
		CAMPAIGN_STATUS_FILTERS.forEach((status) => {
			const ofStatus = uniqueCampaigns.filter(
				(c) => getCampaignStatusGroup(c) === status
			)
			if (ofStatus.length === 0) {
				state[status] = 'none'
				return
			}
			const selectedOfStatus = ofStatus.filter((c) =>
				draftSelection.some((s) => s.id === c.id)
			).length
			state[status] =
				selectedOfStatus === ofStatus.length
					? 'all'
					: selectedOfStatus > 0
						? 'some'
						: 'none'
		})
		return state
	}, [uniqueCampaigns, draftSelection])

	// Bulk select / deselect every campaign of a status group — exactly like
	// togglePlatform in the Ad-accounts selector.
	const toggleStatus = (status: CampaignStatusFilter) => {
		const ofStatus = uniqueCampaigns.filter(
			(c) => getCampaignStatusGroup(c) === status
		)
		if (ofStatus.length === 0) return
		const allSelected = ofStatus.every((c) =>
			draftSelection.some((s) => s.id === c.id)
		)
		if (allSelected) {
			const ids = new Set(ofStatus.map((c) => c.id))
			setDraftSelection((prev) => prev.filter((c) => !ids.has(c.id)))
		} else {
			setDraftSelection((prev) => {
				const existing = new Set(prev.map((c) => c.id))
				const toAdd = ofStatus.filter((c) => !existing.has(c.id))
				return [...prev, ...toAdd]
			})
		}
	}

	const allVisibleSelected =
		visibleCampaigns.length > 0 &&
		visibleCampaigns.every((c) =>
			draftSelection.some((s) => s.id === c.id)
		)
	const someVisibleSelected =
		!allVisibleSelected &&
		visibleCampaigns.some((c) =>
			draftSelection.some((s) => s.id === c.id)
		)

	const handleOpen = () => {
		// The applied selection (selectedCampaigns) already defaults to all
		// campaigns on a fresh page load — see navbar-breadcrumbs.
		setDraftSelection([...selectedCampaigns])
		setSearchQuery('')
		setIsOpen(true)
	}

	useEffect(() => {
		if (!isOpen) return
		const handleClickOutside = (e: MouseEvent) => {
			if (
				containerRef.current &&
				!containerRef.current.contains(e.target as Node)
			) {
				onApply(draftSelection)
				setIsOpen(false)
				setSearchQuery('')
			}
		}
		const handleEscape = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				setIsOpen(false)
				setSearchQuery('')
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		document.addEventListener('keydown', handleEscape)
		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
			document.removeEventListener('keydown', handleEscape)
		}
	}, [isOpen, draftSelection, onApply])

	const toggleCampaign = (campaign: CampaignDTO) => {
		setDraftSelection((prev) => {
			const exists = prev.some((c) => c.id === campaign.id)
			return exists
				? prev.filter((c) => c.id !== campaign.id)
				: [...prev, campaign]
		})
	}

	const handleSelectAll = () => {
		if (allVisibleSelected) {
			const visibleIds = new Set(visibleCampaigns.map((c) => c.id))
			setDraftSelection((prev) =>
				prev.filter((c) => !visibleIds.has(c.id))
			)
		} else {
			setDraftSelection((prev) => {
				const existing = new Set(prev.map((c) => c.id))
				const toAdd = visibleCampaigns.filter(
					(c) => !existing.has(c.id)
				)
				return [...prev, ...toAdd]
			})
		}
	}

	const isAllSelected =
		totalCount > 0 && selectedCampaigns.length === totalCount
	const label = isAllSelected
		? 'All campaigns'
		: `${selectedCampaigns.length} campaign${selectedCampaigns.length === 1 ? '' : 's'}`

	return (
		<div className="relative flex min-w-0" ref={containerRef}>
			<BreadcrumbTrigger
				icon={
					<Announcement02 className="size-4 text-[var(--text-secondary,#535255)]" />
				}
				label={label}
				onClick={() => {
					if (totalCount === 0) return
					isOpen ? setIsOpen(false) : handleOpen()
				}}
				isOpen={isOpen}
			/>

			{isOpen && (
				<div className="absolute top-full left-0 mt-1 z-50">
					{/* Figma frame is 393px, but Chrome renders the pill count digits a
				    few px wider than Figma — 406px keeps all three status pills on
				    one row for 3-digit counts (they wrap beyond that). */}
				<MenuList className="w-[406px] flex flex-col max-h-[400px] !p-[10px]" role="listbox">
						{/* Header: "Campaigns • X of Y selected" */}
						<div className="flex items-center gap-[4px] px-[4px] leading-[16px]">
							<span className="font-['Host_Grotesk',sans-serif] text-[12px] leading-[16px] text-[var(--text-secondary,#535255)] whitespace-nowrap">
								Campaigns
							</span>
							<span className="font-['Host_Grotesk',sans-serif] text-[12px] leading-[16px] text-[var(--text-tertiary,#9f9ea3)]">
								•
							</span>
							<span className="font-['Host_Grotesk',sans-serif] text-[12px] leading-[16px] text-[var(--text-tertiary,#9f9ea3)] whitespace-nowrap">
								{selectedCount} of {totalCount} selected
							</span>
						</div>

						{/* Active / Paused / Removed status filter pills */}
						<div className="mt-[12px]">
							<CampaignStatusPills
								counts={statusCounts}
								isSelected={(status) =>
									statusSelectionState[status] !== 'none'
								}
								onToggle={toggleStatus}
							/>
						</div>

						{/* Search */}
						<div className="mt-[12px]">
							<Input
								size="sm"
								state="default"
								placeholder="Search for campaigns..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
							/>
						</div>

						{/* Select All — full-bleed too so its checkbox lines up with the
						    campaign rows below (which fill the popover width). */}
						<ListItem
							className="-mx-[10px]"
							label="Select all"
							leadingControl={
								<div className="flex items-center" onClick={(e) => e.stopPropagation()}>
									<Checkbox
										checked={allVisibleSelected}
										indeterminate={someVisibleSelected}
										onChange={handleSelectAll}
										size="sm"
									/>
								</div>
							}
							onClick={handleSelectAll}
							weight="medium"
						/>

						{/* Full-bleed: cancel the MenuList's 10px padding so the divider
						    and the scrollable list (incl. its scrollbar) reach the popover
						    edges instead of looking clipped inside a padding gutter. */}
						<MenuListDivider className="-mx-[10px]" />

						{/* Campaign list */}
						<div className="flex-1 min-h-0 overflow-y-auto -mx-[10px] -mb-[10px]">
							{visibleCampaigns.length > 0 ? (
								sortedVisibleCampaigns.map((campaign) => {
									const isChecked = draftSelection.some(
										(c) => c.id === campaign.id
									)
									const platform =
										(adAccountPlatformMap[
											campaign.ad_account_id
										] as
											| 'GADS'
											| 'FADS'
											| 'BADS') || 'GADS'
									const statusGroup =
										getCampaignStatusGroup(campaign)

									return (
										<ListItem
											key={campaign.id}
											label={campaign.name}
											leadingControl={
												<div className="flex items-center" onClick={(e) => e.stopPropagation()}>
													<Checkbox
														checked={isChecked}
														onChange={() => toggleCampaign(campaign)}
														size="sm"
													/>
												</div>
											}
											leadingIcon={
												<PlatformIcon
													platform={platform}
													className="w-4 h-4"
												/>
											}
											trailingIcon={
												statusGroup ? (
													<CampaignStatusIcon
														status={statusGroup}
													/>
												) : undefined
											}
											onClick={() =>
												toggleCampaign(campaign)
											}
										/>
									)
								})
							) : (
								<div className="px-[8px] py-[8px]">
									<span className="font-['Host_Grotesk',sans-serif] text-[14px] leading-[18px] text-[var(--text-tertiary,#9f9ea3)]">
										No campaigns found
									</span>
								</div>
							)}
						</div>
					</MenuList>
				</div>
			)}
		</div>
	)
}
